/**
 * Where the figures came from - on the Overview, beside what needs attention.
 *
 * An imported model's historical figures are not all equally vouched for. Most are what the
 * filing PRINTS, read by code and proved by the statement's own totals; some come from a
 * note proved against the line it analyses; a few printed cells were misread and put right
 * by the arithmetic around them; and what nothing printed vouches for is still the AI's
 * reading of the filing. A user should know which is which before trusting a figure, so
 * the import records it (inputs.figureSources) and this says it, the reading last and in
 * amber. Renders nothing for a model that was not imported.
 */

import { useState } from 'react';
import type { FigureSourceKind, ModelInputsV2 } from '../../../types/financial-model-v2';

export const SOURCE_GROUPS: Array<{
  kind: FigureSourceKind;
  heading: string;
  tone: string;
}> = [
  {
    kind: 'printed',
    heading: 'As the filing prints them, proved by its own totals',
    tone: 'border-green-200 bg-green-50',
  },
  {
    kind: 'notes',
    heading: 'From notes and statements proved against the balance sheet',
    tone: 'border-green-200 bg-green-50',
  },
  {
    kind: 'corrected',
    heading: 'Printed figures the arithmetic corrected',
    tone: 'border-sky-200 bg-sky-50',
  },
  {
    kind: 'reading',
    heading: "Still the AI's reading - nothing printed vouches for these",
    tone: 'border-amber-200 bg-amber-50',
  },
];

/** The groups that have something to say, in the order a reader should meet them. */
export function sourceGroups(inputs: Pick<ModelInputsV2, 'figureSources'>) {
  const sources = inputs.figureSources ?? {};
  return SOURCE_GROUPS.map((group) => ({ ...group, entries: sources[group.kind] ?? [] })).filter(
    (group) => group.entries.length > 0,
  );
}

export function SourcesPanel({ inputs }: { inputs: Pick<ModelInputsV2, 'figureSources'> }) {
  const [open, setOpen] = useState(false);
  const groups = sourceGroups(inputs);
  if (groups.length === 0) return null;
  const fromReading = groups.find((group) => group.kind === 'reading')?.entries.length ?? 0;
  return (
    <section className="mt-4 rounded-xl border border-gray-200 bg-white px-4 py-3" aria-label="Where the figures came from">
      <button
        type="button"
        className="flex w-full items-center justify-between text-left"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        <span className="text-sm font-semibold text-gray-900">Where the figures came from</span>
        <span className="text-xs text-gray-600">
          {fromReading
            ? `${fromReading} ${fromReading === 1 ? 'part' : 'parts'} still from the AI's reading`
            : 'Every historical figure from the filing'}
          <span className="ml-2">{open ? 'Hide' : 'Show'}</span>
        </span>
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          {groups.map((group) => (
            <div key={group.kind} className={`rounded-lg border px-3 py-2 ${group.tone}`}>
              <h4 className="text-xs font-semibold text-gray-900">{group.heading}</h4>
              <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs text-gray-700">
                {group.entries.map((entry) => (
                  <li key={entry}>{entry}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
